import type {
  GetConfigFileStructureQuery, LocalizedConfigBlockDto, LocalizedConfigProfileDto,
  LocalizedConfigProfileStructureDto, LocalizedElementDto,
} from '~/types/graphql';
import {
  type ConfigElement,
  type ConfigElementOption,
  type ConfigModule,
  type HeadlineGroup,
  type ModuleCategory,
} from '~/types/config';
import { useI18n } from 'vue-i18n';
import { useToast } from "primevue/usetoast";
import { translateMatchingPattern } from '~/Common/useI18nTranslations';
import { extractCategoryInfo, getCategoryXpath } from '~/Common/ConfigXPaths';
import { cleanText } from '~/Common/StringOperations';


/**
 * Builds a lookup map for fast element access by ID
 * @param configFileStructure - The configuration file structure to build the map from
 * @return A Map where keys are element IDs and values are objects containing the element and its module info
 */
export function buildElementLookupMap(configFileStructure: GetConfigFileStructureQuery['getConfigFileStructure']):
  Map<number, { element: LocalizedElementDto; moduleInfo: LocalizedConfigBlockDto }> {
  const newMap = new Map<number, { element: LocalizedElementDto; moduleInfo: LocalizedConfigBlockDto }>();

  for (const block of configFileStructure) {
    if (block.elements) {
      for (const element of block.elements) {
        newMap.set(element.id, { element, moduleInfo: block });
      }
    }
  }

  return newMap;
}

export function buildProfileElementLookupMap(
  blocks: LocalizedConfigProfileStructureDto[]
) {
  const map = new Map<number, { element: LocalizedConfigProfileDto; moduleInfo: LocalizedConfigProfileStructureDto }>();
  for (const block of blocks) {
    for (const el of block.elements ?? []) {
      map.set(el.id, { element: el, moduleInfo: block });
    }
  }
  return map;
}

/**
 * Compares the current configuration structure with the configuration values using a lookup map
 * @param elementLookUpMap - Map for fast element lookup by element path
 * @param configValues - An array of tuples where each tuple contains the element path and the new value.
 * @return An array of objects containing the ID and new value for each changed element.
 */
export function getDifferenceToConfigFileStructure(
  elementLookUpMap: ReadonlyMap<number, { element: any; moduleInfo: { id: number } }>,
  configValues: [string, string][]
): {id: number, elementValue: string}[] {
  const changes: { id: number; elementValue: string }[] = [];

  // Extract the current configuration values from the lookup map
  const pathToIdMap = new Map<string, { id: number, elementValue: string }>();
  for (const [id, { element }] of elementLookUpMap) {
    if (element.elementPath) {
      pathToIdMap.set(element.elementPath, { id, elementValue: element.elementValue });
    }
  }

  // Compare the configuration values with the current structure
  for (const [elementPath, newValue] of configValues) {
    const found = pathToIdMap.get(elementPath);
    if (found && found.elementValue !== newValue) {
      changes.push({ id: found.id, elementValue: newValue });
    }
  }

  return changes;
}

/**
 * Maps configuration elements to an array of objects containing the value and name for each required field.
 * @param elements - The configuration elements to map.
 * @return An array of objects containing the value and name for each required field.
 */
export function getElementsValuesNames(elements: ConfigElement[]): { value: any; name: string }[] {
  return elements.map(el => ({
    value: el.elementValue ?? '',
    name: el.elementPath
  }));
}

/**
 * Get the names of elements categorized by their XPath.
 * If an element's name matches the expected pattern for a category name,
 * it will be included in the result.
 * @param block - The configuration block containing elements.
 * @return A record where keys are category XPaths and values are the names of elements in that category.
 */
export function getCategoryNamesFromElements(block: LocalizedConfigBlockDto): Record<string, string>;
export function getCategoryNamesFromElements(block: LocalizedConfigProfileStructureDto): Record<string, string>;
export function getCategoryNamesFromElements(
  block: LocalizedConfigBlockDto | LocalizedConfigProfileStructureDto
): Record<string, string> {
  const categoryNames: Record<string, string> = {};
  const elements = (block?.elements ?? []) as Array<{
    elementPath?: string | null;
    elementName?: string | null;
    elementValue?: string | null;
  }>;

  for (const element of elements) {
    const path = element.elementPath;
    if (!path) continue;

    const categoryXpath = getCategoryXpath(path);

    // Look for a "*_name" element that names the category (connection_name, realm_name, mapping_name, etc.)
    const categoryInfo = extractCategoryInfo(path);
    if (!categoryInfo) continue;

    const nameElementKey = `${categoryInfo.type.replace(/s$/, '')}_name`;
    if (element.elementName === nameElementKey && element.elementValue) {
      categoryNames[categoryXpath] = cleanText(String(element.elementValue));
    }
  }

  return categoryNames;
}
/**
 * Extracts XPaths from categories
 * @param categories - Array of ModuleCategory objects
 * @return Array of XPaths from categories
 */
export function getCategoryXPaths(categories: ModuleCategory[]): string[] {
  return categories
    .filter(cat => cat.xpath)
    .map(cat => cat.xpath!);
}


/**
 * Calculates the total number of elements in an array of modules
 * @param modules - Array of ConfigModule objects
 * @return Total count of elements across all modules
 */
export function countElementsInModules(modules: ConfigModule[]): number {
  return modules.reduce((total, module) => {
    return total + Object.values(module.categories).reduce((categoryTotal, category) => {
      return categoryTotal + category.headlineGroups.reduce((headlineTotal, group) => {
        return headlineTotal + group.elements.length;
      }, 0);
    }, 0);
  }, 0);
}


/**
 * Parse options from element_translations field for select-type elements
 * @param options - String format options, typically JSON
 * @returns Array of options with value and label
 */
export function parseElementOptions (options: string): ConfigElementOption[] {
  if (!options) return [];

  try {
    // Parse JSON string to object
    const optionsObj = JSON.parse(options);

    // If optionsObj is already an array of objects with value and label, return it
    if (Array.isArray(optionsObj) && optionsObj.length > 0 && 'value' in optionsObj[0] && 'label' in optionsObj[0]) {
      return optionsObj;
    }

    // Convert the object to an array of objects with value and label
    return Object.entries(optionsObj).map(([key, value]) => {
      // If value is an object, it might contain translations
      if (typeof value === 'object' && value !== null) {
        return {
          value: key,
          label: (value as any).en || Object.values(value as object)[0] || key,
          translations: value as Record<string, string>
        } as ConfigElementOption;
      }

      // If value is a string, use it as the label
      return {
        value: key,
        label: value as string
      } as ConfigElementOption;
    });
  } catch (e) {
    useToast().add({
      severity: 'error',
      summary: useI18n().t('common.toast.error'),
      detail: useI18n().t('common.toast.error.loading'),
    });
    return [];
  }
}

/**
 * Groups configuration elements by their headlines without sorting.
 * @param elements - The elements to group.
 * @param t
 * @returns An array of HeadlineGroup objects.
 */
export function groupElementsByHeadline(
  elements: ConfigElement[],
  t: (k: string, p?: Record<string, any>) => string = (s) => s
): HeadlineGroup[]  {
  // Group elements by headline
  const headlineMap = new Map<string, ConfigElement[]>();

  // Initialize with default headline if needed
  const defaultHeadline = t('config.form.settings');
  headlineMap.set(defaultHeadline, []);

  // Group elements by headline
  elements.forEach(element => {
    const headline = translateMatchingPattern(
      element.headline || defaultHeadline,
      'config.form',
      /^(realm|connection|mapping)\s+(.+)$/i,
      /^(settings|realm|connection|mapping)$/i,
      t
    );

    if (!headlineMap.has(headline)) {
      headlineMap.set(headline, []);
    }
    headlineMap.get(headline)!.push(element);
  });

  // Convert map to array and filter out empty groups
  return Array.from(headlineMap.entries())
    .map(([headline, elements]) => ({
      headline,
      elements
    }))
    .filter(group => group.elements.length > 0);
}

/**
 * Sorts elements by order and translation.
 */
function sortElements(elements: ConfigElement[]): void {
  elements.sort((a, b) => {
    // Sort by order first, then by translation
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order || a.translation.localeCompare(b.translation);
    }
    return a.translation.localeCompare(b.translation);
  });
}

/**
 * Sorts headline groups by the minimum order of their elements.
 */
function sortHeadlineGroups(headlineGroups: HeadlineGroup[]): void {
  // Sort headline groups by the order of their first element (which is already the minimum after element sorting)
  headlineGroups.sort((a, b) => {
    // Get order from first element (minimum order after sorting)
    const orderA = a.elements[0]?.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.elements[0]?.order ?? Number.MAX_SAFE_INTEGER;

    // Sort by order, fall back to headline comparison if orders are equal
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.headline.localeCompare(b.headline);
  });
}

/**
 * Sorts modules by translation alphabetically.
 * @param modules - An array of ConfigModule objects to sort.
 * @return A sorted array of ConfigModule objects.
 */
function sortModules(modules: ConfigModule[]): ConfigModule[] {
  return [...modules].sort((a, b) => a.translation.localeCompare(b.translation));
}

/**
 * Sorts categories within a module: "settings" first, then alphabetical.
 * @param categories - A record of ModuleCategory objects to sort.
 * @return An array of tuples where each tuple contains the category key and the sorted ModuleCategory.
 */
function sortCategories(categories: Record<string, ModuleCategory>): [string, ModuleCategory][] {
  return Object.entries(categories).sort((a, b) => {
    // "settings" category always comes first
    if (a[0].toLowerCase() === 'settings') return -1;
    if (b[0].toLowerCase() === 'settings') return 1;

    // Otherwise sort alphabetically by categoryName
    return a[1].categoryName.localeCompare(b[1].categoryName);
  });
}

/**
 * Centrally sorts all configuration data: modules, categories, headline groups, and elements.
 * This is the only function that should handle sorting in the configuration system.
 * @param modules - An array of ConfigModule objects to sort.
 * @return A sorted array of ConfigModule objects with all nested data sorted.
 */
export function sortConfigData(modules: ConfigModule[]): ConfigModule[] {
  // Sort modules by translation alphabetically
  const sortedModules = sortModules(modules);

  // Process each module's categories
  for (const module of sortedModules) {
    // Get sorted category entries and process them in one pass
    const sortedCategories = sortCategories(module.categories)
      .map(([categoryKey, category]) => {
        // Sort headline groups and elements if they exist
        if (category.headlineGroups) {
          // Sort elements within each headline group FIRST
          category.headlineGroups.forEach(group => {
            sortElements(group.elements);
          });

          // Then sort headline groups (now first element has minimum order)
          sortHeadlineGroups(category.headlineGroups);
        }
        return [categoryKey, category];
      });

    // Reassign sorted categories
    module.categories = Object.fromEntries(sortedCategories);
  }

  return sortedModules;
}

/**
 * Filters config values by excluding the given categories
 * @param configValues - The configuration values
 * @param categoriesToFilter - Array of ModuleCategory objects to filter out
 * @return Filtered array of config value entries
 */
export function filterConfigValues(
  configValues: Record<string, string>,
  categoriesToFilter: ModuleCategory[]
): [string, string][] {
  const categoryXPaths = getCategoryXPaths(categoriesToFilter);

  return Object.entries(configValues).filter(([elementPath]) => {
    return !categoryXPaths.some(xpath => elementPath.startsWith(xpath));
  });
}

/**
 * Filter modules by specific fields.
 * @param fields - The fields to filter by, represented as an array of strings.
 * @param modulesToSearch - The array of ConfigModule objects to search through.
 * @returns An array of ConfigModule objects that contain the specified fields.
 */
export function filterModulesByFields(fields: string[], modulesToSearch: ConfigModule[]): ConfigModule[] {
  const fieldPaths = new Set(fields);

  return modulesToSearch
    .map(module => {
      // Filter categories to only include those with elements
      const filteredCategories: Record<string, ModuleCategory> = {};
      Object.entries(module.categories).forEach(([categoryName, category]) => {

        // Get all elements from the category's headline groups with paths in fieldPaths
        const allElements = category.headlineGroups.flatMap(group => group.elements);
        const configElements = allElements.filter(el => fieldPaths.has(el.elementPath));

        if (configElements.length > 0) {
          // Group the filtered elements by their headlines
          const filteredGroups = groupElementsByHeadline(configElements);

          // Add the category to the filtered categories if it has any groups
          filteredCategories[categoryName] = {
            ...category,
            headlineGroups: filteredGroups
          };
        }
      });

      // If the module has any categories with elements, include it
      if (Object.keys(filteredCategories).length > 0) {
        return {
          ...module,
          categories: filteredCategories
        };
      }
      return null;
    })
    .filter(Boolean) as ConfigModule[];
}

/**
 * Flattens a ConfigModule structure into an array for display in tables/grids.
 * @param modules - Array of ConfigModule objects to flatten
 * @param getOriginalValue - Optional function to retrieve original values for comparison
 * @returns Flattened array with module, category and element information
 */
export function flattenConfigModules(
  modules: ConfigModule[],
  getOriginalValue?: (elementId: number) => string
): Array<ConfigElement & {
  moduleName: string;
  moduleId: number;
  categoryName: string;
  originalValue?: string;
}> {
  const flattened: Array<ConfigElement & {
    moduleName: string;
    moduleId: number;
    categoryName: string;
    originalValue?: string;
  }> = [];

  modules.forEach(module => {
    Object.values(module.categories).forEach(category => {
      category.headlineGroups.forEach(headlineGroup => {
        headlineGroup.elements.forEach(element => {
          flattened.push({
            ...element,
            moduleName: module.translation,
            moduleId: module.moduleId,
            categoryName: category.translation || category.categoryName,
            originalValue: getOriginalValue ? getOriginalValue(element.id) : undefined
          });
        });
      });
    });
  });
  return flattened;
}

/**
 * Groups differences by module ID using a lookup map
 * @param differences - Array of differences with id and elementValue (readonly)
 * @param elementLookupMap - Map for fast element lookup (readonly)
 * @returns Map with moduleId as key and differences array as value
 */
export function groupDifferencesByModule<T extends { readonly id: number }>(
  differences: readonly T[],
  elementLookupMap: ReadonlyMap<number, { readonly element: any; readonly moduleInfo: { readonly id: number } }>
): Map<number, T[]> {
  const differencesByModule = new Map<number, T[]>();

  differences.forEach(diff => {
    const lookupResult = elementLookupMap.get(diff.id);
    if (lookupResult) {
      const moduleId = lookupResult.moduleInfo.id;
      if (!differencesByModule.has(moduleId)) {
        differencesByModule.set(moduleId, []);
      }
      differencesByModule.get(moduleId)!.push(diff);
    }
  });

  return differencesByModule;
}
